import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { PursuitService } from '@mono/proto-clients';
import { Button, SpecraCheckbox, SpecraFormField } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { connectTransport } from '../../../lib/connect/transport';
import { displayAnswer, parseAnswers } from '../hooks/useQualification';
const pursuitClient = createClient(PursuitService, connectTransport);
/**
 * Selective handoff (SAD-480): a human opens a Pursuit from a "pursue"
 * Qualification and ticks which context carries over. The opening request id is
 * minted once per deliberate open and kept until it succeeds, so retrying after
 * an error returns the same Pursuit; opening again afterwards is a deliberate
 * new path. The Opportunity is linked, never copied.
 */ export function QualificationHandoff({ opportunityId, evaluationId, pursuitName, rationale, answersJson }) {
    const queryClient = useQueryClient();
    const requestId = useRef(null);
    const [selected, setSelected] = useState([]);
    const answers = Object.entries(parseAnswers(answersJson));
    const items = [
        ...rationale ? [
            {
                key: 'rationale',
                label: `Rationale: ${rationale}`
            }
        ] : [],
        ...answers.map(([id, v])=>({
                key: `answer:${id}`,
                label: `${id}: ${displayAnswer(v)}`
            }))
    ];
    const open = useMutation({
        mutationFn: ()=>{
            var _requestId;
            (_requestId = requestId).current ?? (_requestId.current = crypto.randomUUID());
            return pursuitClient.openPursuit({
                openingRequestId: requestId.current,
                opportunityId,
                qualificationId: evaluationId,
                name: pursuitName,
                handoff: {
                    evaluationId,
                    selectedKeys: selected
                }
            });
        },
        onSuccess: ()=>{
            requestId.current = null;
            void queryClient.invalidateQueries({
                queryKey: [
                    'qualification',
                    'events'
                ]
            });
            void queryClient.invalidateQueries({
                queryKey: [
                    'capture-list'
                ]
            });
        },
        onError: ()=>toast.error('Could not open Pursuit; retry keeps the same request')
    });
    const toggle = (key)=>setSelected((s)=>s.includes(key) ? s.filter((k)=>k !== key) : [
                ...s,
                key
            ]);
    const opened = open.data?.summary?.captureProjectId;
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Open a Pursuit",
        className: "space-y-2",
        "data-testid": Testids.qualification.handoffSection,
        children: [
            /*#__PURE__*/ _jsx("h4", {
                children: "Open a Pursuit"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Choose what carries over. Unchecked context stays on the Qualification; the Opportunity is linked, not copied."
            }),
            items.map((it)=>/*#__PURE__*/ _jsx(SpecraFormField, {
                    label: it.label,
                    children: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                        checked: selected.includes(it.key),
                        onCheckedChange: ()=>toggle(it.key),
                        "data-testid": Testids.qualification.handoffChoice,
                        "data-key": it.key
                    })
                }, it.key)),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        size: "sm",
                        disabled: open.isPending,
                        onClick: ()=>open.mutate(),
                        "data-testid": Testids.qualification.handoffOpen,
                        children: open.isPending ? 'Opening…' : opened ? 'Open another pursuit path' : 'Open Pursuit'
                    }),
                    opened && /*#__PURE__*/ _jsx("a", {
                        href: `/capture/${opened}`,
                        "data-testid": Testids.qualification.handoffResult,
                        "data-capture-project-id": opened,
                        "data-created": String(open.data?.created),
                        children: "Pursuit opened"
                    })
                ]
            })
        ]
    });
}
